import type {
  ClientCoverageSummary,
  CoverageStatus,
  DeviceIssue,
  DeviceRecord,
  DeviceStatus,
  ExecutiveSummary,
  IssueType,
  IssueSeverity,
  OsType,
} from '../data/types';

/**
 * Local API entity shapes — mirrors the backend AuditResultEntity from Table Storage.
 * Defined here rather than imported from backend src/ because the dashboard
 * is a separate application with its own dependency boundary.
 */
export interface ApiAuditResult {
  partitionKey: string;
  rowKey: string;
  clientName: string;
  companyName?: string;
  deviceName: string;
  osType: string;
  automateStatus: string;
  huntressStatus: string;
  threatlockerStatus: string;
  msp360Status: string;
  addigyStatus?: string;
  coveragePercentage: number;
  hasCriticalIssue: boolean;
  /** A required platform is missing and not excused by an expected gap (absent on old rows). */
  hasOpenGap?: boolean;
  /** JSON Record<platform, device name as reported by that platform>. */
  platformNames?: string;
  /** JSON Record<platform, match tier> (serial / hostname / alternate_name / fuzzy / …). */
  matchTiers?: string;
  /** Hardware serial used for matching. */
  serialNumber?: string;
  auditRunId: string;
  auditTimestamp: string;
  /** Whether this device has been retired in Automate */
  isRetired?: boolean;
  /** ISO 8601 timestamp when the device was retired */
  retiredDate?: string;
  /** Comment explaining why the device was retired */
  retireComment?: string;
  /** Location name from Automate for geographic grouping */
  locationName?: string;
}

/**
 * Local API entity shape — mirrors the backend CriticalIssueEntity from Table Storage.
 */
export interface ApiCriticalIssue {
  partitionKey: string;
  rowKey: string;
  deviceName: string;
  clientName: string;
  issueType: string;
  severity: string;
  description: string;
  resolved: boolean;
  auditRunId: string;
  auditTimestamp: string;
}

/**
 * Derives a CoverageStatus from a numeric coverage percentage.
 *
 * Thresholds:
 *   - <80%  → critical
 *   - 80-95% → warning
 *   - ≥95%  → healthy
 */
function deriveCoverageStatus(coveragePct: number): CoverageStatus {
  if (coveragePct < 80) return 'critical';
  if (coveragePct < 95) return 'warning';
  return 'healthy';
}

/**
 * Maps an array of raw API audit result entities to per-client coverage summaries.
 *
 * Groups entities by `partitionKey` (client ID), computes device counts per
 * platform, averages the coverage percentage, and derives a status classification.
 *
 * Note: `coverageTrend` is always `[]` — the current API only returns the latest
 * audit run, not historical snapshots. Trend data requires a future multi-run API.
 */
export function mapToClientSummaries(entities: ApiAuditResult[]): ClientCoverageSummary[] {
  const clientMap = new Map<string, ApiAuditResult[]>();

  for (const entity of entities) {
    const existing = clientMap.get(entity.partitionKey);
    if (existing) {
      existing.push(entity);
    } else {
      clientMap.set(entity.partitionKey, [entity]);
    }
  }

  const summaries: ClientCoverageSummary[] = [];

  for (const [partitionKey, group] of clientMap) {
    const sample = group[0];
    // Prefer an entity with companyName (Automate devices have it, orphans don't).
    const companyName = group.find((e) => e.companyName)?.companyName || '';
    // Exclude retired devices from coverage calculations.
    const activeDevices = group.filter((e) => !e.isRetired);
    const totalDevices = activeDevices.length;
    // Orphaned = in security platforms but no RMM (automateStatus missing, no Addigy)
    const orphanedCount = activeDevices.filter((e) => e.automateStatus === 'missing').length;
    // Exclude orphaned devices from coverage — they need decommissioning, not agent install
    const managedDevices = activeDevices.filter((e) => e.automateStatus !== 'missing');

    const huntressCount = activeDevices.filter((e) => e.huntressStatus === 'present').length;
    const threatlockerCount = activeDevices.filter(
      (e) => e.threatlockerStatus === 'present'
    ).length;
    const msp360Count = activeDevices.filter((e) => e.msp360Status === 'present').length;
    const addigyCount = activeDevices.filter((e) => e.addigyStatus === 'present').length;

    const coveragePct =
      managedDevices.length > 0
        ? Math.round(
            (managedDevices.reduce((sum, e) => sum + e.coveragePercentage, 0) /
              managedDevices.length) *
              10
          ) / 10
        : 0;

    // Count unique non-empty location names from active devices only.
    const locations = new Set(
      activeDevices.map((e) => e.locationName).filter((name): name is string => Boolean(name))
    );

    summaries.push({
      id: partitionKey,
      clientName: sample.clientName,
      companyName,
      totalDevices,
      huntressCount,
      threatlockerCount,
      msp360Count,
      addigyCount,
      orphanedCount,
      coveragePct,
      status: deriveCoverageStatus(coveragePct),
      lastAuditAt: sample.auditTimestamp,
      // Trend data not available from single-run API — requires historical multi-run endpoint.
      coverageTrend: [],
      locationCount: locations.size > 0 ? locations.size : undefined,
    });
  }

  return summaries.sort((a, b) => a.clientName.localeCompare(b.clientName));
}

/**
 * Aggregates all audit result entities into a single executive summary.
 *
 * Calls `mapToClientSummaries` internally for per-client metrics. Issue counts
 * are derived from platform status fields: a device counts as "missing" for a
 * platform when its status is 'missing' (and not 'not_applicable').
 *
 * Note: `coverageTrend` is always `[]` — see `mapToClientSummaries` for rationale.
 */
export function mapToExecutiveSummary(entities: ApiAuditResult[]): ExecutiveSummary {
  if (entities.length === 0) {
    return {
      overallCoveragePct: 0,
      clientsBelowThreshold: 0,
      totalDevicesAudited: 0,
      lastAuditAt: new Date().toISOString(),
      coverageTrend: [],
      issuesByType: {
        missing_huntress: 0,
        missing_threatlocker: 0,
        missing_msp360: 0,
        orphaned_device: 0,
        av_unhealthy: 0,
        av_not_protected: 0,
        retired_with_active_agents: 0,
      },
      clientsWithMostIssues: [],
      retiredDeviceCount: 0,
    };
  }

  // Separate retired from active devices.
  const retiredCount = entities.filter((e) => e.isRetired).length;
  const activeEntities = entities.filter((e) => !e.isRetired);

  const clientSummaries = mapToClientSummaries(entities);

  const overallCoveragePct =
    activeEntities.length > 0
      ? Math.round(
          activeEntities.reduce((sum, e) => sum + e.coveragePercentage, 0) / activeEntities.length
        )
      : 0;

  const clientsBelowThreshold = clientSummaries.filter((c) => c.coveragePct < 95).length;

  const lastAuditAt = entities.reduce(
    (max, e) => (e.auditTimestamp > max ? e.auditTimestamp : max),
    entities[0].auditTimestamp
  );

  // Count issues by type from raw entity platform status fields (active devices only).
  const issuesByType: Record<IssueType, number> = {
    missing_huntress: 0,
    missing_threatlocker: 0,
    missing_msp360: 0,
    orphaned_device: 0,
    av_unhealthy: 0,
    av_not_protected: 0,
    retired_with_active_agents: 0,
  };

  for (const entity of activeEntities) {
    if (entity.huntressStatus === 'missing') {
      issuesByType.missing_huntress++;
    }
    if (entity.threatlockerStatus === 'missing') {
      issuesByType.missing_threatlocker++;
    }
    if (entity.msp360Status === 'missing') {
      issuesByType.missing_msp360++;
    }
    if (entity.automateStatus === 'missing') {
      issuesByType.orphaned_device++;
    }
  }

  // Compute issue count per client from devices with hasCriticalIssue flag.
  const issueCountByClient = new Map<
    string,
    { clientId: string; clientName: string; issueCount: number }
  >();

  for (const entity of activeEntities) {
    if (entity.hasCriticalIssue) {
      const existing = issueCountByClient.get(entity.partitionKey);
      if (existing) {
        existing.issueCount++;
      } else {
        issueCountByClient.set(entity.partitionKey, {
          clientId: entity.partitionKey,
          clientName: entity.clientName,
          issueCount: 1,
        });
      }
    }
  }

  const clientsWithMostIssues = Array.from(issueCountByClient.values())
    .sort((a, b) => b.issueCount - a.issueCount)
    .slice(0, 5);

  return {
    overallCoveragePct,
    clientsBelowThreshold,
    totalDevicesAudited: activeEntities.length,
    lastAuditAt,
    coverageTrend: [],
    issuesByType,
    clientsWithMostIssues,
    retiredDeviceCount: retiredCount,
  };
}

/** Parse a JSON-encoded Record<string, string> column; undefined when absent or malformed. */
function parseJsonRecord(json: string | undefined): Record<string, string> | undefined {
  if (!json) return undefined;
  try {
    const parsed = JSON.parse(json) as unknown;
    return typeof parsed === 'object' && parsed !== null && !Array.isArray(parsed)
      ? (parsed as Record<string, string>)
      : undefined;
  } catch {
    return undefined;
  }
}

/**
 * Converts raw API audit result entities to DeviceRecord objects for the device grid.
 *
 * Platform presence is only included when the status is not 'not_applicable' —
 * this prevents misleading "not covered" indicators for platforms that don't apply
 * to a given device (e.g., MSP360 on devices without the EDF flag set).
 */
export function mapToDeviceRecords(
  entities: ApiAuditResult[],
  opts?: { isAddigyClient?: boolean }
): DeviceRecord[] {
  const isAddigy = opts?.isAddigyClient ?? false;

  return entities.map((entity): DeviceRecord => {
    const platforms: Record<string, boolean> = {};

    // RMM platform: Addigy clients use Addigy as source of truth instead of Automate.
    if (isAddigy) {
      platforms['Addigy'] = entity.automateStatus === 'present';
    } else {
      platforms['Automate'] = entity.automateStatus === 'present';
    }

    // Huntress — universally required.
    platforms['Huntress'] = entity.huntressStatus === 'present';

    // Conditional platforms — only include when they apply to this device.
    if (entity.threatlockerStatus !== 'not_applicable') {
      platforms['ThreatLocker'] = entity.threatlockerStatus === 'present';
    }
    if (entity.msp360Status !== 'not_applicable') {
      platforms['MSP360'] = entity.msp360Status === 'present';
    }
    // Show standalone Addigy chip only for non-Addigy-primary clients (rare edge case).
    if (!isAddigy && entity.addigyStatus && entity.addigyStatus !== 'not_applicable') {
      platforms['Addigy'] = entity.addigyStatus === 'present';
    }

    // A required platform shown as missing means the device is NOT healthy, whatever the
    // discrepancy's severity (missing ThreatLocker is 'high', not 'critical'). Rows written
    // before hasOpenGap existed fall back to the platform map, which only contains
    // required platforms.
    const hasOpenGap =
      entity.hasOpenGap ??
      Object.entries(platforms).some(
        ([name, present]) => !present && name !== 'Automate' && name !== 'Addigy'
      );

    // Retired status takes priority over all other status checks.
    let status: DeviceStatus;
    if (entity.isRetired) {
      status = 'retired';
    } else if (entity.automateStatus === 'missing') {
      status = 'orphaned';
    } else if (entity.hasCriticalIssue || hasOpenGap) {
      status = 'issue';
    } else {
      status = 'healthy';
    }

    return {
      id: entity.rowKey,
      deviceName: entity.deviceName,
      osType: (entity.osType as OsType) ?? 'unknown',
      platforms,
      lastSeen: entity.auditTimestamp,
      status,
      platformNames: parseJsonRecord(entity.platformNames),
      matchTiers: parseJsonRecord(entity.matchTiers),
      serialNumber: entity.serialNumber || undefined,
      isRetired: entity.isRetired,
      retiredDate: entity.retiredDate,
      retireComment: entity.retireComment,
      locationName: entity.locationName,
    };
  });
}

/**
 * Maps platform issue type strings to human-readable platform names.
 */
function affectedPlatformFromIssueType(issueType: string): string {
  switch (issueType) {
    case 'missing_huntress':
      return 'Huntress';
    case 'missing_threatlocker':
      return 'ThreatLocker';
    case 'missing_msp360':
      return 'MSP360';
    case 'orphaned_device':
      return 'Automate';
    case 'av_unhealthy':
    case 'av_not_protected':
      return 'Antivirus';
    case 'retired_with_active_agents':
      return 'Automate';
    default:
      return 'Unknown';
  }
}

/**
 * Maps backend severity strings to dashboard IssueSeverity values.
 *
 * 'high' is treated as 'critical' because high-severity issues require
 * immediate attention, matching the critical classification.
 */
function mapSeverity(severity: string): IssueSeverity {
  switch (severity) {
    case 'critical':
    case 'high':
      return 'critical';
    case 'medium':
      return 'warning';
    case 'low':
    default:
      return 'info';
  }
}

/**
 * Converts an array of critical issue entities to DeviceIssue objects.
 *
 * Note: `osType` defaults to 'unknown' — CriticalIssueEntity does not carry
 * OS information. If OS is needed, callers should join with device records.
 */
export function mapToDeviceIssues(issues: ApiCriticalIssue[]): DeviceIssue[] {
  return issues.map(
    (issue): DeviceIssue => ({
      id: issue.rowKey,
      deviceName: issue.deviceName,
      osType: 'unknown' as OsType,
      issueType: issue.issueType as IssueType,
      severity: mapSeverity(issue.severity),
      affectedPlatform: affectedPlatformFromIssueType(issue.issueType),
      description: issue.description,
    })
  );
}
